import React, { useState, useCallback, useEffect } from "react";

import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import {
  ContentBlockFormModal,
  ContentBlocksTable,
  AssignContentBlockToPlaylistModal,
  AssignContentBlockAsEmergencyToDisplayModal,
  ContentTypeSelector,
  TemplateContentBlockPickerModal,
} from "@/components/features";
import { useContentBlocks } from "@/hooks";
import { ContentBlock } from "@/utils";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const ContentBlocksPage: React.FC<PageProps> = (props: PageProps) => {
  // Local search state
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [selectedContentType, setSelectedContentType] = useState<string | null>(
    ""
  ); // Empty string means "All types"

  // Template picker modal state
  const [templatePickerOpened, setTemplatePickerOpened] =
    useState<boolean>(false);

  // Use the content blocks hook for all data management
  const {
    // Data state
    contentBlock,
    contentBlocks,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    filters,
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingContentBlockId,
    selectedContentBlock,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // UI state management
    setSelectedContentBlock,

    // Error handling
    lastError,
    clearError,
  } = useContentBlocks({
    autoLoad: false,
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [assignModalOpened, setAssignModalOpened] = useState<boolean>(false);
  const [contentBlockToDelete, setContentBlockToDelete] =
    useState<ContentBlock | null>(null);
  const [contentBlockToAssign, setContentBlockToAssign] =
    useState<ContentBlock | null>(null);
  const [emergencyContentBlockToAssign, setEmergencyContentBlockToAssign] =
    useState<ContentBlock | null>(null);

  // Effect to load content blocks on mount and when filters change
  useEffect(() => {
    const newFilters: any = {};

    // Add search filter if keyword is present
    if (searchKeyword.trim()) {
      newFilters.name = searchKeyword.trim();
    }

    // Add content type filter if a specific type is selected
    if (selectedContentType) {
      newFilters.contentType = [selectedContentType];
    }

    list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
  }, [selectedContentType, list, sortBy]);

  // Handle content type filter change
  const handleContentTypeChange = useCallback(
    (value: string | null) => {
      setSelectedContentType(value || "");

      const newFilters = { ...filters };

      // Remove or add content type filter
      if (value) {
        newFilters.contentType = [value];
      } else {
        delete newFilters.contentType;
      }

      // Preserve search filter if present
      if (searchKeyword.trim()) {
        newFilters.searchKeyword = searchKeyword.trim();
      }

      // Update filters and reload data
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [filters, searchKeyword, setFilters, list, sortBy]
  );

  // Handle search with debouncing
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchKeyword(value);

      // Apply search filter
      const newFilters = { ...filters };
      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      } else {
        delete newFilters.searchKeyword;
      }

      // Preserve content type filter if present
      if (selectedContentType) {
        newFilters.contentType = [selectedContentType];
      }

      // Update filters and reload data
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [selectedContentType, filters, setFilters, list, sortBy]
  );

  // Handle add content block button click
  const handleAddContentBlock = useCallback(() => {
    setSelectedContentBlock(null);
    setFormModalOpened(true);
  }, [setSelectedContentBlock]);

  // Handle add from template button click
  const handleAddFromTemplate = useCallback(() => {
    setTemplatePickerOpened(true);
  }, []);

  // Handle template picker close
  const handleTemplatePickerClose = useCallback(() => {
    setTemplatePickerOpened(false);
  }, []);

  // Handle template selection - create content block from template
  const handleTemplateSelect = useCallback(
    async (templateContentBlock: ContentBlock) => {
      setSelectedContentBlock(templateContentBlock);
      setFormModalOpened(true);
    },
    [create, refresh]
  );

  // Handle edit content block
  const handleEditContentBlock = useCallback(
    (contentBlock: ContentBlock) => {
      setSelectedContentBlock(contentBlock);
      setFormModalOpened(true);
    },
    [setSelectedContentBlock]
  );

  // Handle duplicate content block
  const handleDuplicateContentBlock = useCallback(
    (contentBlock: ContentBlock) => {
      setSelectedContentBlock({ ...contentBlock, id: "" });
      setFormModalOpened(true);
    },
    [setSelectedContentBlock]
  );

  // Handle view content block (could navigate to content block details page)
  const handleViewContentBlock = useCallback((contentBlock: ContentBlock) => {
    // TODO: Navigate to content block details page or show details modal
    console.log("View content block:", contentBlock);
  }, []);

  // Handle assign content block to displays
  const handleAssignContentBlock = useCallback((contentBlock: ContentBlock) => {
    setContentBlockToAssign(contentBlock);
    setAssignModalOpened(true);
  }, []);

  const handleAssignEmergencyContentBlock = useCallback(
    (contentBlock: ContentBlock) => {
      setEmergencyContentBlockToAssign(contentBlock);
    },
    []
  );

  // Handle assign modal close
  const handleAssignModalClose = useCallback(() => {
    setAssignModalOpened(false);
    setContentBlockToAssign(null);
    setEmergencyContentBlockToAssign(null);
  }, []);

  // Handle remove content block button click
  const handleRemoveContentBlockClick = useCallback(
    (contentBlock: ContentBlock) => {
      setContentBlockToDelete(contentBlock);
      setDeleteModalOpened(true);
    },
    []
  );

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (contentBlockToDelete?.id) {
      const success = await remove(contentBlockToDelete.id);
      if (success) {
        setDeleteModalOpened(false);
        setContentBlockToDelete(null);
      }
    }
  }, [contentBlockToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setContentBlockToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedContentBlock(null);
    clearError();
  }, [setSelectedContentBlock, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle content block creation
  const handleCreateContentBlock = useCallback(
    async (contentBlockData: any) => {
      const result = await create(contentBlockData);
      if (result) {
        setFormModalOpened(false);
        setSelectedContentBlock(null);
      }
      return result;
    },
    [create, setSelectedContentBlock]
  );

  // Handle content block update
  const handleUpdateContentBlock = useCallback(
    async (contentBlockId: string, contentBlockData: any) => {
      const result = await update(contentBlockId, contentBlockData);
      if (result) {
        setFormModalOpened(false);
        setSelectedContentBlock(null);
      }
      return result;
    },
    [update, setSelectedContentBlock]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions, filters and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-content-block"
                  iconName="Plus"
                  onClick={handleAddContentBlock}
                  disabled={isCreating || !!removingContentBlockId || loading}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="add-from-template"
                  iconName="FileText"
                  onClick={handleAddFromTemplate}
                  disabled={isCreating || !!removingContentBlockId || loading}
                >
                  Add from Template
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || !!removingContentBlockId || loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "filters-and-search",
              items: [
                <ContentTypeSelector
                  key="content-type-filter"
                  value={selectedContentType}
                  onChange={handleContentTypeChange}
                  showAllOption
                  allOptionLabel="All types"
                  placeholder="Filter by type"
                  size="sm"
                  style={{ minWidth: 180 }}
                  disabled={loading}
                />,
                <SearchInput
                  key="search"
                  placeholder="Search content blocks..."
                  value={searchKeyword}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                  disabled={loading && !searchKeyword}
                />,
              ],
              align: "right",
              gap: "sm",
            },
          ]}
        />

        {/* Content blocks table */}
        <ContentBlocksTable
          contentBlocks={contentBlocks}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditContentBlock={handleEditContentBlock}
          onDuplicateContentBlock={handleDuplicateContentBlock}
          onRemoveContentBlock={handleRemoveContentBlockClick}
          onViewContentBlock={handleViewContentBlock}
          onAssignContentBlock={handleAssignContentBlock}
          onAssignEmergencyContentBlock={handleAssignEmergencyContentBlock}
          removingContentBlockId={removingContentBlockId}
          editedContentBlockId={contentBlock?.id}
          size="md"
        />

        {/* Empty state when no content blocks */}
        <EmptyState
          display={isEmpty && !loading}
          title="No content blocks found"
          description={
            searchKeyword || selectedContentType
              ? "Try adjusting your filters or search criteria"
              : "Start by adding your first content block"
          }
          actionIcon="Plus"
          actionLabel="Add Content Block"
          onAction={handleAddContentBlock}
        />
      </Stack>

      {/* Content block form modal for create/edit */}
      <ContentBlockFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        contentBlock={selectedContentBlock}
        onCreateContentBlock={handleCreateContentBlock}
        onUpdateContentBlock={handleUpdateContentBlock}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Template picker modal */}
      <TemplateContentBlockPickerModal
        open={templatePickerOpened}
        onClose={handleTemplatePickerClose}
        onChange={handleTemplateSelect}
        title="Select Template"
      />

      {/* Assign content block to displays modal */}
      {contentBlockToAssign && (
        <AssignContentBlockToPlaylistModal
          opened={assignModalOpened}
          onClose={handleAssignModalClose}
          contentBlock={contentBlockToAssign}
        />
      )}

      {emergencyContentBlockToAssign && (
        <AssignContentBlockAsEmergencyToDisplayModal
          opened={!!emergencyContentBlockToAssign}
          onClose={handleAssignModalClose}
          contentBlock={emergencyContentBlockToAssign}
        />
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Content Block"
        message={`Are you sure you want to delete "${contentBlockToDelete?.name}"?`}
        details="This action cannot be undone. The content block will be permanently removed from all displays."
        confirmInProgress={removingContentBlockId === contentBlockToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
